import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

const API_BASE = process.env.REACT_APP_API_URL;

const serviceCategories = [
  { type: 'Doctor Visit', emoji: '🩺', color: '#F44336', bg: '#FFEBEE', desc: 'Home doctor visit or clinic appointment' },
  { type: 'Medicine Delivery', emoji: '💊', color: '#9C27B0', bg: '#F3E5F5', desc: 'Get medicines delivered to your door' },
  { type: 'Taxi Assistance', emoji: '🚕', color: '#FF9800', bg: '#FFF3E0', desc: 'Safe transport for seniors' },
  { type: 'Home Repair', emoji: '🔧', color: '#795548', bg: '#EFEBE9', desc: 'Plumbing, electrical, carpentry help' },
  { type: 'Physiotherapy', emoji: '🏃', color: '#4CAF50', bg: '#E8F5E9', desc: 'Home physiotherapy sessions' },
  { type: 'Other Help', emoji: '🤲', color: '#2196F3', bg: '#E3F2FD', desc: 'Any other assistance you need' }
];

const statusConfig = {
  pending: { label: 'Pending', color: '#E65100', bg: '#FFF3E0', emoji: '⏳' },
  confirmed: { label: 'Confirmed', color: '#1B5E20', bg: '#E8F5E9', emoji: '✅' },
  completed: { label: 'Completed', color: '#0D47A1', bg: '#E3F2FD', emoji: '🎉' },
  cancelled: { label: 'Cancelled', color: '#B71C1C', bg: '#FFEBEE', emoji: '❌' }
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const Services = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/services`, { headers });
      setRequests(res.data.requests || []);
    } catch (err) {
      console.error('Fetch services error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const openModal = (service) => {
    setSelectedService(service);
    setDescription('');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!selectedService) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/api/services`, {
        service_type: selectedService.type,
        description: description.trim()
      }, { headers });

      setRequests([res.data.request, ...requests]);
      setShowModal(false);
      setSelectedService(null);
      setDescription('');
      toast.success(`${selectedService.emoji} Request submitted! We'll confirm soon.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (requestId) => {
    try {
      const res = await axios.put(`${API_BASE}/api/services/${requestId}`, { status: 'cancelled' }, { headers });
      setRequests(requests.map(r => r.id === requestId ? res.data.request : r));
      toast.success('Request cancelled');
    } catch (err) {
      toast.error('Failed to cancel request');
    }
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #FF9800 0%, #FFA726 100%)',
        padding: '16px',
        color: 'white'
      }}>
        <h2 style={{ color: 'white', marginBottom: '4px' }}>🤝 Services</h2>
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px' }}>
          Request help from our volunteer network
        </p>
      </div>

      <div className="page-content">
        {/* Service Categories Grid */}
        <h3 style={{ marginBottom: '12px' }}>Request a Service</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '24px' }}>
          {serviceCategories.map((service) => (
            <button
              key={service.type}
              onClick={() => openModal(service)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '18px 12px',
                background: service.bg,
                border: `2px solid ${service.color}20`,
                borderRadius: '16px',
                cursor: 'pointer',
                textAlign: 'center',
                fontFamily: "'Hind', 'Poppins', sans-serif",
                transition: 'all 0.2s',
                gap: '8px'
              }}
              onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.96)'}
              onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <span style={{ fontSize: '32px' }}>{service.emoji}</span>
              <span style={{ fontSize: '14px', fontWeight: '700', color: service.color, lineHeight: '1.3' }}>
                {service.type}
              </span>
              <span style={{ fontSize: '11px', color: '#888', lineHeight: '1.3' }}>
                {service.desc}
              </span>
            </button>
          ))}
        </div>

        {/* My Requests */}
        <h3 style={{ marginBottom: '12px' }}>My Requests</h3>
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <div className="empty-title">No requests yet</div>
            <div className="empty-text">Tap any service above to request help</div>
          </div>
        ) : (
          requests.map((req) => {
            const status = statusConfig[req.status] || statusConfig.pending;
            const service = serviceCategories.find(s => s.type === req.service_type);
            return (
              <div key={req.id} className="card" style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '28px' }}>{service?.emoji || '🤲'}</span>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#2D2D2D' }}>
                        {req.service_type}
                      </div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        {formatDate(req.created_at)}
                      </div>
                    </div>
                  </div>
                  <span style={{
                    background: status.bg,
                    color: status.color,
                    fontSize: '12px',
                    fontWeight: '700',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}>
                    {status.emoji} {status.label}
                  </span>
                </div>
                {req.description && (
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px', paddingLeft: '38px' }}>
                    {req.description}
                  </p>
                )}
                {req.status === 'pending' && (
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ width: 'auto', marginLeft: '38px', color: '#F44336', borderColor: '#F44336' }}
                    onClick={() => handleCancel(req.id)}
                  >
                    Cancel Request
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Service Request Modal */}
      {showModal && selectedService && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-content">
            <div className="modal-handle"></div>

            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '8px' }}>
                {selectedService.emoji}
              </span>
              <h3 style={{ fontSize: '20px', color: '#2D2D2D', marginBottom: '4px' }}>
                {selectedService.type}
              </h3>
              <p style={{ fontSize: '14px', color: '#888' }}>{selectedService.desc}</p>
            </div>

            <div className="form-group">
              <label className="form-label">Additional Details (optional)</label>
              <textarea
                className="form-input"
                placeholder={`E.g., I need help with ${selectedService.type.toLowerCase()}...`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                autoFocus
              />
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
              style={{ marginBottom: '10px' }}
            >
              {submitting ? '⏳ Submitting...' : `📨 Submit Request`}
            </button>

            <button className="btn btn-ghost" onClick={() => setShowModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default Services;
